/**
 * Utility functions for encoding/decoding flight search parameters to/from URL
 */

export interface SearchParams {
    tripType: "RT" | "OW" | "MC"; // Round Trip, One Way, Multi City
    origin: string;
    destination: string;
    departureDate: string; // YYYYMMDD format
    returnDate?: string; // YYYYMMDD format
    adults: number;
    children: number;
    infants: number;
    youths: number;
}

/**
 * Encodes search parameters into URL format
 * Example: RT_OSL-NYC-20250923-20250930_a2c0i0y0
 */
export const encodeSearchParams = (params: SearchParams): string => {
    const {
        tripType,
        origin,
        destination,
        departureDate,
        returnDate,
        adults,
        children,
        infants,
        youths = 0,
    } = params;

    // Format: TRIPTYPE_ORIGIN-DEST[-DEPDATE[-RETDATE]]_aXcXiXyX
    let searchString = tripType + "_";

    // Add route
    searchString += `${origin}-${destination}`;

    // Add dates (format YYYYMMDD)
    const depDate = departureDate.replace(/-/g, "");
    searchString += `-${depDate}`;

    if (tripType === "RT" && returnDate) {
        const retDate = returnDate.replace(/-/g, "");
        searchString += `-${retDate}`;
    }

    // Add passenger counts
    searchString += `_a${adults}c${children}i${infants}y${youths}`;

    return searchString;
};

/**
 * Decodes URL search string back to search parameters
 * Example: RT_OSL-NYC-20250923-20250930_a2c0i0y0
 */
export const decodeSearchParams = (
    searchString: string
): SearchParams | null => {
    try {
        // Split by underscores
        const parts = searchString.split("_");
        if (parts.length !== 3) return null;

        const tripType = parts[0] as "RT" | "OW" | "MC";
        const routeAndDates = parts[1].split("-");
        const passengers = parts[2];

        // Parse route and dates
        const origin = routeAndDates[0];
        const destination = routeAndDates[1];
        const departureDate = formatDateString(routeAndDates[2]);
        const returnDate = routeAndDates[3]
            ? formatDateString(routeAndDates[3])
            : undefined;

        // Parse passengers
        const adultsMatch = passengers.match(/a(\d+)/);
        const childrenMatch = passengers.match(/c(\d+)/);
        const infantsMatch = passengers.match(/i(\d+)/);
        const youthsMatch = passengers.match(/y(\d+)/);

        return {
            tripType,
            origin,
            destination,
            departureDate,
            returnDate,
            adults: adultsMatch ? parseInt(adultsMatch[1]) : 1,
            children: childrenMatch ? parseInt(childrenMatch[1]) : 0,
            infants: infantsMatch ? parseInt(infantsMatch[1]) : 0,
            youths: youthsMatch ? parseInt(youthsMatch[1]) : 0,
        };
    } catch (error) {
        console.error("Error decoding search params:", error);
        return null;
    }
};

/**
 * Formats date string from YYYYMMDD to YYYY-MM-DD
 */
const formatDateString = (dateStr: string): string => {
    if (dateStr.length !== 8) return dateStr;
    return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(
        6,
        8
    )}`;
};

/**
 * Formats date for display (e.g., "Tue 23 Sep")
 */
export const formatDateForDisplay = (dateStr: string): string => {
    const date = new Date(dateStr + "T00:00:00");
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
    ];

    return `${days[date.getDay()]} ${date.getDate()} ${
        months[date.getMonth()]
    }`;
};

/**
 * Gets trip type label
 */
export const getTripTypeLabel = (tripType: "RT" | "OW" | "MC"): string => {
    switch (tripType) {
        case "RT":
            return "Round trip";
        case "OW":
            return "One way";
        case "MC":
            return "Multi journey";
        default:
            return "Round trip";
    }
};
